import { useEffect, useState } from "react";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IPrincipal, ITableColumn } from "@interfaces/common.interfaces";
import { IReportSectionProps } from "components/reports-common/report-section/report-section.props";
import ReportSection from "components/reports-common/report-section/report-section";
import {
    IInactiveWorkspacesByOwnerReportDetails,
    IInactiveWorkspacesByOwnerReportItem,
    IInactiveWorkspacesByOwnerReportRequestModel
} from "@interfaces/reports/inactive-workspaces-reports.interfaces";
import InactiveWorkspacesByOwnerReportSummary from "components/reports/usage-reports/inactive-workspaces-by-owner-report/inactive-workspaces-by-owner-report-summary";
import PrincipalPicker from "components/controls/principal-picker/principal-picker";
import MinMaxInput from "components/common/inputs/min-max-input";
import WorkspaceLinkCell from "components/reports-common/report-results-grid-cells/workspace-link-cell/workspace-link-cell";
import PrincipalsCell from "components/reports-common/report-results-grid-cells/principals-cell/principals-cell";
import DateTimeCell from "components/reports-common/report-results-grid-cells/date-time-cell/date-time-cell";
import { getWorkspaceTypeKey } from "lib/workspaces";
import { escapeForExport } from "lib/utils";
import { usePPA } from "context/ppa-context";
import { PPAPermissions } from "lib/permissions";

const reportUrl: string = "/api/reports/inactive-workspaces-by-owner";

export default function InactiveWorkspacesByOwnerReport() {
    const { t } = useL10n<ILocaleStrings>();
    const { currentUser, hasPermission } = usePPA();

    const [model, setModel] = useState<IInactiveWorkspacesByOwnerReportRequestModel>({
        inactivityDays: 30
    });
    const [selectedUser, setSelectedUser] = useState<IPrincipal>();
    const [mineOnly, setMineOnly] = useState<boolean>(false);
    const [inactivityDays, setInactivityDays] = useState<number>(30);

    useEffect(() => {
        setSelectedUser(currentUser);
        if (
            hasPermission(PPAPermissions.RequestInactiveWorkspacesByOwnerReportMineOnly) &&
            !hasPermission(PPAPermissions.RequestInactiveWorkspacesByOwnerReportAny)
        ) {
            setMineOnly(true);
        }
    }, []);

    const onUserChange = (value?: IPrincipal) => {
        setModel({ ...model, owner: { upn: value?.upn } });
        setSelectedUser(value);
    };

    const onInactivityDaysChange = (value: number) => {
        setInactivityDays(value);
        setModel({
            ...model,
            inactivityDays: value
        });
    };

    // Define table columns
    const cols: Array<ITableColumn<IInactiveWorkspacesByOwnerReportItem>> = [
        {
            key: "workspaceName",
            label: t("ppaWorkspace"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IInactiveWorkspacesByOwnerReportItem) => (
                <WorkspaceLinkCell
                    name={item.workspaceName}
                    url={item.workspaceUrl}
                    template={item.workspaceTemplate}
                    type={item.workspaceType}
                />
            )
        },
        {
            key: "workspaceUrl",
            label: t("ppaWorkspaceUrl"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "workspaceType",
            label: t("ppaWorkspaceType"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IInactiveWorkspacesByOwnerReportItem) => (
                t(getWorkspaceTypeKey(item.workspaceType))
            ),
            getExportValue: (key: string, item: IInactiveWorkspacesByOwnerReportItem) => (
                t(getWorkspaceTypeKey(item.workspaceType))
            )
        },
        {
            key: "workspaceTemplate",
            label: t("ppaWorkspaceTemplate"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "owners",
            label: t("ppaOwners"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IInactiveWorkspacesByOwnerReportItem) => (
                <PrincipalsCell columnId={key} item={item} />
            ),
            getExportValue: (key: string, item: IInactiveWorkspacesByOwnerReportItem) => {
                return escapeForExport((item as any)[key]);
            }
        },
        {
            key: "owners",
            label: t("ppaOwnerUpns"),
            showInGrid: false,
            showInExport: true,
            getExportValue: (key: string, item: IInactiveWorkspacesByOwnerReportItem) => {
                return escapeForExport((item as any)[key].map((o: any) => o.upn));
            }
        },
        {
            key: "accessCount",
            label: t("ppaAccessCount"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 120
        },
        {
            key: "userVisits",
            label: t("ppaUserVisits"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 120
        },
        {
            key: "lastModified",
            label: t("lastModified"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IInactiveWorkspacesByOwnerReportItem) => (
                <DateTimeCell columnId={key} item={item} pattern="PPp" />
            )
        }
    ];

    const isModelValid = (): boolean => {
        const hasOwner = model.owner?.upn ? true : false;
        const hasValidDays = inactivityDays >= 1 && inactivityDays <= 90;
        return hasOwner && hasValidDays;
    };

    const reportSectionProps: IReportSectionProps<IInactiveWorkspacesByOwnerReportItem, IInactiveWorkspacesByOwnerReportDetails> = {
        columns: cols,
        summaryComponent: <InactiveWorkspacesByOwnerReportSummary />,
        reportUrl: reportUrl,
        model: model,
        isModelValid: isModelValid(),
        noItemsMessage: t("ppaInactiveWorkspacesByOwnerReportNoItemsMessage"),
        defaultExportFilenamePrefix: "ppaInactiveWorkspacesByOwnerReportDefaultExportFilenamePrefix"
    }

    return (
        <ReportSection {...reportSectionProps}>
            <MinMaxInput
                value={inactivityDays}
                min={1}
                max={90}
                label={t("ppaInactivityDays")}
                tooltip={t("ppaInactivityDaysTooltip")}
                required={true}
                onChange={onInactivityDaysChange}
            />
            <PrincipalPicker
                onChange={onUserChange}
                model={selectedUser}
                label="ppaWorkspaceRoleTypeOwner"
                readonly={mineOnly}
                principalTypes={["User"]}
            />
        </ReportSection>
    );
}
